<script setup lang="ts">
import Select from '~/components/ui/Select.vue'
import BtnTryRandom from '~/components/pages/home/BtnTryRandom.vue'
import { useResultStore } from '~/store/result'
import SelectNumbers from '~/components/pages/home/NewComponents/SelectNumbers.vue'
import { IDS_LOTTERIES_CLASSIC } from '~/utils/constants/home.js'
import GetNotified from '~/components/pages/results/Header/GetNotified.vue'
import BetModeToggle from '~/components/pages/results/MultipleBets/BetModeToggle.vue'
import AmountPill from '~/components/pages/home/AmountPill.vue'
import InfoPlayLottery from '~/components/pages/results/Header/InfoPlayLottery.vue'
import { ID_KENO_FR, ID_EL_GORDO_NAVIDAD } from '~/utils/constants/idsLotteries'

const { t: $t } = useI18n()
const localePath = useLocalePath()

type Item = (string | number | null)[]

interface NumbersBlock {
	first: Item
	last?: Item | any
}

defineOptions({
	name: 'CardOpen',
})

const resultStore = useResultStore()
const codeLotteryRef = ref<{ clearAll: (scope?: string) => Promise<void> } | null>(null)

interface Props {
	country?: any
	lottery?: any
	lotteries?: any[]
	countries: any[]
	isDisabledBtn?: boolean
	random?: () => void
	loaderBtnRandom?: boolean
	showResult?: () => void
	rules?: any
	blockNumbers: NumbersBlock | any
	updateNumbers?: (value: NumbersBlock) => void
	updateCountry?: (value: any) => void
	updateLottery?: (value: any) => void
	showMultipleBets?: boolean
	amount?: number
}

const emit = defineEmits(['changeMode', 'updateAmount'])

const props = withDefaults(defineProps<Props>(), {
	country: null,
	lottery: null,
	lotteries: () => [],
	isDisabledBtn: false,
	loaderBtnRandom: false,
	rules: () => ({}),
	random: () => {},
	showResult: () => {},
	updateNumbers: () => {},
	updateCountry: () => {},
	updateLottery: () => {},
	showMultipleBets: false,
	amount: 1,
})

const getAmount = computed({
	get() {
		return props.amount
	},
	set(newValue) {
		emit('updateAmount', newValue)
	},
})

const getNumbers = computed({
	get() {
		return props.blockNumbers
	},
	set(newValue) {
		props.updateNumbers(newValue)
	},
})

const showBtnClear = computed(() => {
	if (IDS_LOTTERIES_CLASSIC.includes(props.rules?.id)) return false
	if (props.blockNumbers === null) return false
	const { first = [], last = [] } = props.blockNumbers
	const firstBlock = first ? first.some((num: number) => num !== null) : false
	const lastBlock = last ? last.some((num: number) => num !== null) : false
	return firstBlock || lastBlock
})

const clearAll = async () => {
	if (codeLotteryRef.value === null) return
	await codeLotteryRef.value.clearAll('results')
}

defineExpose({
	codeLotteryRef,
})
</script>

<template>
	<div class="px-4 sm:px-8 py-5 sm:py-10 flex flex-col gap-12 w-full">
		<BetModeToggle
			v-if="props.showMultipleBets"
			class="block smm:hidden"
			@click.stop
			@change="emit('changeMode', $event)"
		/>
		<header class="flex flex-col justify-between gap-8 2xl:flex-row 2xl:items-center">
			<div
				class="flex flex-col sm:flex-row items-center gap-5 w-full"
				@click.stop
			>
				<Select
					:value="props.country || undefined"
					:list="props.countries ?? []"
					bg-list="bg-secondary-700 smm:bg-secondary-800"
					class="select-custom md:max-w-80"
					font-size="text-base"
					height="h-14"
					:translate="true"
					@change="props.updateCountry"
				/>
				<Select
					:value="props.lottery ?? undefined"
					:list="props.lotteries"
					:disabled="props.lotteries.length === 1"
					:show-icon-leading="props.lottery !== null"
					bg-list="bg-secondary-700 smm:bg-secondary-800"
					:placeholder="$t('Select a lottery')"
					class="select-custom md:max-w-80"
					font-size="text-base"
					height="h-14"
					@change="props.updateLottery"
				>
					<template #icon-leading>
						<InfoPlayLottery :lottery="props.lottery">
							<template #icon>
								<UIcon
									name="i-material-symbols:info-outline-rounded"
									class="size-6 pl-5 text-white cursor-pointer hover:text-white/80"
								/>
							</template>
						</InfoPlayLottery>
					</template>
				</Select>

				<GetNotified
					v-if="ID_EL_GORDO_NAVIDAD !== props.lottery?.id"
					:lottery="props.lottery"
					:numbers="props.blockNumbers"
					:show-description="!props.isDisabledBtn"
					:disabled="props.lottery === null"
					class="w-full sm:w-auto sm:hidden"
					class-btn="min-h-12"
				/>
			</div>

			<div class="order-first flex justify-between items-center gap-4 sm:gap-8 2xl:order-last">
				<div class="block 2xl:hidden">
					<h1 class="font-semibold text-base text-white leading-6">{{ $t('Hello') }}!</h1>
					<p class="text-sm leading-5 text-white text-opacity-50">{{ $t('Your numbers') }}</p>
				</div>
				<div class="flex items-center gap-4 sm:gap-8">
					<BetModeToggle
						v-if="props.showMultipleBets"
						class="hidden smm:flex"
						@click.stop
						@change="emit('changeMode', $event)"
					/>
					<UButton
						icon="i-ep:arrow-down-bold"
						size="sm"
						color="white"
						class="text-white rotate-180"
						square
						variant="link"
					/>
				</div>
			</div>
		</header>
		<section>
			<SelectNumbers
				ref="codeLotteryRef"
				v-model="getNumbers"
				class-container-header="flex flex-col sm:flex-row items-center gap-5 sm:gap-0 sm:items-star"
				class-container-numbers="flex items-center"
				:show-header-numbers="IDS_LOTTERIES_CLASSIC.includes(props.rules?.id)"
				:rules="props.rules"
				:show-animation-floating-balls="IDS_LOTTERIES_CLASSIC.includes(props.rules?.id)"
				class-container-block-numbers="flex flex-wrap justify-start gap-2"
				:is-mutiple-bets="resultStore.isMutipleBets"
				@update="props.updateNumbers"
				@click.stop
			>
				<template #extra-section>
					<AmountPill
						v-if="props.lottery?.id === ID_KENO_FR"
						v-model="getAmount"
						:options="[1, 2, 3, 5, 10]"
						class="sm:max-w-[350px]"
					/>
				</template>
			</SelectNumbers>
		</section>
		<footer
			v-if="props.lottery"
			class="relative flex flex-col justify-between gap-8 smm:gap-5 lg:flex-row sm:items-center"
			@click.stop
		>
			<div class="flex flex-col sm:flex-row sm:items-start lg:items-center gap-5 w-full lg:w-auto">
				<BtnTryRandom
					:on-click="props.random"
					:loading="props.loaderBtnRandom"
					class="h-12 w-full sm:flex-1 sm:min-w-0 lg:flex-none lg:w-auto"
				/>

				<UButton
					:disabled="props.isDisabledBtn"
					:loading="resultStore.loading"
					:to="localePath('/results')"
					color="white"
					variant="soft"
					:label="$t('Check past results')"
					icon="i-mage:stars-c"
					leading
					:ui="{
						rounded: 'rounded-full',
					}"
					class="px-5 text-base font-semibold flex justify-center w-full h-12 sm:flex-1 sm:min-w-0 lg:flex-none lg:w-auto"
					:class="[
						{
							'hover:bg-opacity-90 bg-primary-50 border-none text-success-600':
								!props.isDisabledBtn || !resultStore.loading,
							'border border-neutral-400 bg-neutral-500 text-white': props.isDisabledBtn || resultStore.loading,
						},
					]"
					@click="props.showResult"
				/>
			</div>
			<div
				class="flex sm:items-center justify-end gap-4 order-first sm:order-last w-full lg:w-auto pt-0 sm:pt-10 lg:pt-0"
			>
				<UButton
					v-if="showBtnClear"
					variant="link"
					:label="$t('Clear all')"
					class="text-white text-base hover:text-white hover:no-underline px-8 sm:px-5 rounded-full hover:bg-white/[0.1] transition-all duration-300 w-full sm:w-auto flex justify-center min-h-12 smm:h-auto"
					@click="clearAll"
				/>
				<GetNotified
					v-if="ID_EL_GORDO_NAVIDAD !== props.lottery?.id"
					:lottery="props.lottery"
					:numbers="props.blockNumbers"
					:show-description="!props.isDisabledBtn"
					:disabled="props.lottery === null"
					class="hidden sm:flex"
					class-btn="min-h-12"
				/>
			</div>
		</footer>
	</div>
</template>
